import { useCallback, useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useAppSelector } from 'src/salon/app/hook';

// 評価者（審査員）の識別フック。
//
// 審査員ログイン(company.role === 'judge')でアクセスしている場合は、
// companies.judgeUids / judgeInvites 経由で解決済みの実uidと登録名(company.judgeName)をそのまま使い、
// 自己申告モーダルは一切表示しない（setName/clearは何もしない no-op）。
//
// 会社共有ログイン(company.role === 'owner')でアクセスしている場合は、
// スタッフ個別アカウントを持たない共有ログインのため「誰が評価したか」を区別する
// 実認証の仕組みがない。旧仕様（auth.currentUser.uid + users/{uid}の氏名）の代わりに、
// ブラウザで評価者名を一度入力してもらい localStorage に保存する自己申告方式で代替する。
// 注意: こちらは認証ではなくUI上の自己申告に過ぎない。別の名前を入力すれば
// 別人として振る舞えてしまうため、なりすまし防止の仕組みではない。

export type JudgeIdentity = {
  judgeId: string;
  name: string;
  isSet: boolean;
  setName: (name: string) => void;
  clear: () => void;
};

const STORAGE_PREFIX = 'memorii-works.evaluation.judge.';

function slugify(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-一-龠ぁ-んァ-ヶー]/gi, '');
  return slug || 'judge';
}

function randomSuffix(): string {
  return Math.random().toString(36).slice(2, 8);
}

type StoredJudge = { judgeId: string; name: string };

function readStorage(storageKey: string): StoredJudge | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as StoredJudge) : null;
  } catch {
    return null;
  }
}

export function useJudgeIdentity(): JudgeIdentity {
  const company = useAppSelector((state) => state.company.company);
  const storageKey = `${STORAGE_PREFIX}${company?.id ?? 'unknown'}`;

  const [ stored, setStored ] = useState<StoredJudge | null>(() => readStorage(storageKey));

  useEffect(() => {
    setStored(readStorage(storageKey));
  }, [storageKey]);

  const setName = useCallback((name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const value: StoredJudge = { judgeId: `${slugify(trimmed)}-${randomSuffix()}`, name: trimmed };
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // localStorageが使えない環境ではセッション中のみ有効(stateのみ保持)
    }
    setStored(value);
  }, [storageKey]);

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // noop
    }
    setStored(null);
  }, [storageKey]);

  // 認証済み審査員セッションでは、自己申告(stored)を使わず実uid＋登録名を返す。
  // すべてのフックはここまでで既に呼び出し済みのため、戻り値の分岐のみで済み
  // Reactのフックルールに違反しない。
  if (company.role === 'judge') {
    const authUid = getAuth().currentUser?.uid ?? '';
    return {
      judgeId: authUid,
      name: company.judgeName,
      isSet: !!authUid,
      setName: () => {},
      clear: () => {},
    };
  }

  return {
    judgeId: stored?.judgeId ?? '',
    name: stored?.name ?? '',
    isSet: !!stored,
    setName,
    clear,
  };
}
